import { Injectable } from '@angular/core';

/**
 * @name FrontFiltersService
 * @desc Servicio que centraliza el filtro rapido de header (dx-header-filter) del pivot grid
 * usado por las consultas de Vines (ConsultBySiteMexicoPage y MyConsultsModal).
 * La seleccion se lee/escribe directamente sobre los campos del PivotGridDataSource
 * (filterValues/filterType), no simulando clicks sobre el DOM del popup; en movil se
 * aplica la misma seleccion sobre los registros con filterRows.
 * Antes esta logica estaba duplicada y hardcodeada unicamente para el campo Desc_Estatus;
 * FRONT_FILTER_FIELDS es la unica lista que hay que tocar para soportar un campo adicional.
 */
const FRONT_FILTER_FIELDS = ['Desc_Estatus', 'Desc_CentroDistribucion'];

@Injectable({
  providedIn: 'root',
})
export class FrontFiltersService {
  readonly fields = FRONT_FILTER_FIELDS;

  private fieldsInConsult(consult: any): string[] {
    return this.fields.filter(
      (field) =>
        consult.cols.indexOf(field) !== -1 ||
        consult.rows.indexOf(field) !== -1,
    );
  }

  private ensureFrontFiltersObject(consult: any): void {
    if (!consult.frontFilters || Array.isArray(consult.frontFilters)) {
      consult.frontFilters = {};
    }
  }

  /**
   * @name captureFromDataSource
   * @desc Lee de los campos del PivotGridDataSource la seleccion actual del header-filter
   * de cada campo soportado presente en la consulta, y la guarda en consult.frontFilters
   */
  captureFromDataSource(consult: any, dataSource: any): void {
    if (!dataSource) return;
    this.ensureFrontFiltersObject(consult);
    this.fieldsInConsult(consult).forEach((field) => {
      const dsField = dataSource.field(field);
      consult.frontFilters[field] = (dsField && dsField.filterValues) || [];
    });
  }

  /**
   * @name getFilterValues
   * @desc Regresa la seleccion guardada para un campo, para usarse al construir los
   * campos del PivotGridDataSource (filterValues/filterType) antes de renderizar el grid
   */
  getFilterValues(consult: any, fieldName: string): string[] {
    if (this.fields.indexOf(fieldName) === -1) return [];
    const source =
      consult.frontFilters && !Array.isArray(consult.frontFilters)
        ? consult.frontFilters
        : {};
    return source[fieldName] || [];
  }

  /**
   * @name filterRows
   * @desc Regresa (en un arreglo nuevo) solo los registros que cumplen la seleccion guardada de
   * cada campo soportado presente en la consulta. Usado por la lista movil, que no tiene
   * header-filter propio como el PivotGrid
   */
  filterRows(consult: any, rows: any[]): any[] {
    const active = this.fieldsInConsult(consult)
      .map((field) => ({ field, values: this.getFilterValues(consult, field) }))
      .filter((filter) => filter.values.length);
    if (!active.length) return rows;
    return rows.filter((row) =>
      active.every((filter) => filter.values.indexOf(row[filter.field]) !== -1),
    );
  }

  /**
   * @name serializeForBackend
   * @desc Arma el string "Campo;v1~v2|Campo;v3~v4|" para el payload "valores" de guardar/actualizar consulta
   */
  serializeForBackend(consult: any): string {
    const fields = this.fieldsInConsult(consult);
    if (!fields.length) return '';
    const source =
      consult.frontFilters && !Array.isArray(consult.frontFilters)
        ? consult.frontFilters
        : {};
    return (
      fields
        .map((field) => field + ';' + (source[field] || []).join('~'))
        .join('|') + '|'
    );
  }

  /**
   * @name registerFromServerField
   * @desc Extrae hacia consult.frontFilters la seleccion de un campo devuelta por el backend
   * (un segmento de Renglones/Columnas con formato "Campo;orden;v1~v2")
   */
  registerFromServerField(
    consult: any,
    fieldName: string,
    rawValues: string,
  ): void {
    if (this.fields.indexOf(fieldName) === -1 || !rawValues) return;
    this.ensureFrontFiltersObject(consult);
    consult.frontFilters[fieldName] = rawValues.split('~');
  }

  /**
   * @name buildDetailFilterSuffix
   * @desc Arma el sufijo "#Campo$v1,v2" (uno por cada campo con seleccion) que se agrega
   * al filtro de detalle al navegar de la consulta a la lista de vines
   */
  buildDetailFilterSuffix(frontFilters: any): string {
    const source =
      frontFilters && !Array.isArray(frontFilters) ? frontFilters : {};
    return this.fields
      .filter((field) => source[field] && source[field].length)
      .map((field) => '#' + field + '$' + source[field].join(','))
      .join('');
  }
}
